/**
 * MobileReadinessSnapshotsList — mobile list of immutable signed
 * readiness snapshots (Batch 7A).
 *
 * Snapshots are strictly READ-ONLY on mobile: no Edit / Delete / Re-sign
 * control is rendered. Each row exposes only the existing signed-PDF
 * endpoint: GET /dashboard/readiness/signoffs/{id}/pdf.
 *
 * Server-authoritative: signed_at / score / band_label / signed_by_name /
 * signed_by_role / priority_actions_count / areas_performing_strongly /
 * areas_requiring_attention / comment / assessment_generated_at are
 * rendered verbatim.
 */
import React from "react";
import { FileDown, ShieldCheck, Lock } from "lucide-react";

function SnapshotCard({ s, onDownloadPdf }) {
  const signedAt = new Date(s.signed_at).toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
  const generatedAt = s.assessment_generated_at
    ? new Date(s.assessment_generated_at).toLocaleString(undefined, {
        day: "2-digit", month: "short", year: "numeric",
      })
    : "—";
  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-3"
      data-testid={`mobile-signoff-history-${s.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <ShieldCheck className="w-3 h-3 text-emerald-700" />
            <span className="tabular-nums">{signedAt}</span>
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="font-heading text-lg font-semibold tabular-nums text-slate-900" data-testid={`mobile-signoff-score-${s.id}`}>
              {s.score}
            </span>
            <span className="text-[11px] text-slate-500">/ 100</span>
            <span className="text-[12px] text-slate-700 break-words" data-testid={`mobile-signoff-band-${s.id}`}>
              {s.band_label}
            </span>
          </div>
        </div>
        <span
          className="text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-800 border border-emerald-300 px-1.5 py-0.5 rounded inline-flex items-center gap-1 shrink-0"
          data-testid={`mobile-signoff-locked-${s.id}`}
        >
          <Lock className="w-3 h-3" /> Signed
        </span>
      </div>

      <div className="mt-2 text-[12px] text-slate-700 break-words">
        <span className="font-medium">{s.signed_by_name}</span>
        <span className="text-slate-400"> · </span>
        <span>{s.signed_by_role}</span>
      </div>

      <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
        <div className="border border-slate-200 rounded p-2 text-center">
          <div className="font-heading text-base font-semibold tabular-nums text-slate-900">{s.priority_actions_count ?? 0}</div>
          <div className="text-[9px] uppercase tracking-widest text-slate-500 mt-0.5">Priority</div>
        </div>
        <div className="border border-emerald-200 bg-emerald-50/40 rounded p-2 text-center">
          <div className="font-heading text-base font-semibold tabular-nums text-emerald-800">{s.areas_performing_strongly ?? 0}</div>
          <div className="text-[9px] uppercase tracking-widest text-emerald-800 mt-0.5">Strong</div>
        </div>
        <div className="border border-amber-200 bg-amber-50/40 rounded p-2 text-center">
          <div className="font-heading text-base font-semibold tabular-nums text-amber-900">{s.areas_requiring_attention ?? 0}</div>
          <div className="text-[9px] uppercase tracking-widest text-amber-900 mt-0.5">Attention</div>
        </div>
      </div>

      {s.comment && (
        <div className="mt-2 text-[12px] text-slate-600 italic bg-slate-50 border border-slate-200 rounded p-2 break-words whitespace-pre-line">
          &ldquo;{s.comment}&rdquo;
        </div>
      )}

      <div className="mt-2 text-[10px] text-slate-500">
        Assessment generated {generatedAt}
      </div>

      <button
        type="button"
        onClick={() => onDownloadPdf(s)}
        className="mt-3 h-11 w-full inline-flex items-center justify-center gap-1.5 text-[13px] text-emerald-800 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 rounded"
        data-testid={`btn-mobile-signoff-pdf-${s.id}`}
      >
        <FileDown className="w-4 h-4" /> Download signed PDF
      </button>
    </div>
  );
}

export default function MobileReadinessSnapshotsList({ signoffHistory, onDownloadPdf }) {
  const list = Array.isArray(signoffHistory) ? signoffHistory : [];
  if (list.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4 text-[13px] text-slate-500" data-testid="mobile-readiness-no-snapshots">
        No signed readiness assessments yet.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="mobile-readiness-snapshots-list">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold px-1">
        Assessment History ({list.length})
      </div>
      <div className="text-[11px] text-slate-500 px-1">
        Signed assessments are immutable historical records. Values shown are captured verbatim at sign-off time.
      </div>
      {list.map((s) => (
        <SnapshotCard key={s.id} s={s} onDownloadPdf={onDownloadPdf} />
      ))}
    </div>
  );
}
